import { useState, useMemo, useCallback, useRef } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Button,
  Toolbar,
  Pagination,
  Search,
  Badge,
  Filter,
  FilterGroup,
  ContextualMenu,
  ContextualMenuItem,
  DownloadIcon,
  SendIcon,
  TrashIcon,
  FavoriteIcon,
  EditIcon,
  DescriptionIcon,
  CloseIcon,
  SearchIcon,
  FilterIcon,
} from '@forge/common';
import type { FilterOptionCheckboxValue, FilterOptionDateRangeValue, FilterOptionNumberRangeValue } from '@forge/common';

interface FileRecord {
  id: string;
  name: string;
  size: number;
  type: string;
  sender: { id: string; name: string };
  receiver: { id: string; name: string };
  direction: string;
  status: string;
  date: string;
  starred: boolean;
  deleted: boolean;
  checksum: string;
  expires: string;
  tags: string[];
}

interface FileTableProps {
  readonly files: FileRecord[];
  readonly selectedFileId: string | null;
  readonly onRowClick: (file: FileRecord) => void;
  readonly onForward: (files: FileRecord[]) => void;
  readonly onDelete: (fileIds: string[]) => void;
  readonly onToggleStar: (fileId: string) => void;
}

const PAGE_SIZE_OPTIONS = [8, 16, 24, 50];
const DEFAULT_PAGE_SIZE = 8;

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const directionBadgeColor: Record<string, 'green' | 'blue'> = {
  sent: 'green',
  received: 'blue',
};

const directionBadgeText: Record<string, string> = {
  sent: 'Sent',
  received: 'Received',
};

const statusBadgeColor: Record<string, 'green' | 'blue' | 'orange' | 'gray'> = {
  sent: 'green',
  new: 'blue',
  pending: 'orange',
  read: 'gray',
};

const statusBadgeText: Record<string, string> = {
  sent: 'Sent',
  new: 'New',
  pending: 'Pending',
  read: 'Read',
};

const MoreDotsIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
    <circle cx="3" cy="8" r="1.5" />
    <circle cx="8" cy="8" r="1.5" />
    <circle cx="13" cy="8" r="1.5" />
  </svg>
);

const FileTable = ({
  files,
  selectedFileId,
  onRowClick,
  onForward,
  onDelete,
  onToggleStar,
}: FileTableProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<string | undefined>(undefined);
  const [sortDir, setSortDir] = useState<'asc' | 'desc' | undefined>(undefined);
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterDateFrom, setFilterDateFrom] = useState<string | undefined>(undefined);
  const [filterDateTo, setFilterDateTo] = useState<string | undefined>(undefined);
  const [filterOpen, setFilterOpen] = useState(false);
  const stableMenuRefs = useRef<Record<string, React.MutableRefObject<HTMLButtonElement | null>>>({});

  const getMenuRef = useCallback((fileId: string): React.MutableRefObject<HTMLButtonElement | null> => {
    if (!stableMenuRefs.current[fileId]) {
      stableMenuRefs.current[fileId] = { current: null };
    }
    return stableMenuRefs.current[fileId];
  }, []);

  // Apply filters
  const filteredFiles = useMemo(() => {
    let result = files;

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (f) =>
          f.name.toLowerCase().includes(q) ||
          f.sender.name.toLowerCase().includes(q) ||
          f.receiver.name.toLowerCase().includes(q) ||
          f.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }

    if (filterTypes.length > 0) {
      result = result.filter((f) => {
        const ext = f.name.split('.').pop()?.toLowerCase() || '';
        return filterTypes.some((ft) => {
          if (ft === 'pdf') return ext === 'pdf';
          if (ft === 'spreadsheet') return ['xlsx', 'csv'].includes(ext);
          if (ft === 'document') return ['docx', 'md', 'yaml', 'sql'].includes(ext);
          if (ft === 'image') return ['png', 'svg', 'jpg', 'jpeg'].includes(ext);
          if (ft === 'archive') return ['zip', 'tar', 'gz'].includes(ext);
          return false;
        });
      });
    }

    if (filterStatuses.length > 0) {
      result = result.filter((f) => filterStatuses.includes(f.status));
    }

    if (filterDateFrom) {
      const from = new Date(filterDateFrom).getTime();
      result = result.filter((f) => new Date(f.date).getTime() >= from);
    }

    if (filterDateTo) {
      const to = new Date(filterDateTo).getTime();
      result = result.filter((f) => new Date(f.date).getTime() <= to);
    }

    return result;
  }, [files, searchQuery, filterTypes, filterStatuses, filterDateFrom, filterDateTo]);

  // Sort
  const sortedFiles = useMemo(() => {
    if (!sortField || !sortDir) return filteredFiles;
    return [...filteredFiles].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'sender':
          cmp = a.sender.name.localeCompare(b.sender.name);
          break;
        case 'size':
          cmp = a.size - b.size;
          break;
        case 'date':
          cmp = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        default:
          cmp = 0;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [filteredFiles, sortField, sortDir]);

  // Paginate
  const totalPages = Math.ceil(sortedFiles.length / pageSize);
  const pagedFiles = useMemo(
    () => sortedFiles.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [sortedFiles, currentPage, pageSize],
  );

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((_direction: 'asc' | 'desc' | undefined, id: string) => {
    setSortField((prevField) => {
      setSortDir((prevDir) => {
        if (prevField === id && prevDir === 'asc') return 'desc';
        return 'asc';
      });
      return id;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === pagedFiles.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pagedFiles.map((f) => f.id)));
    }
  }, [selectedIds, pagedFiles]);

  const handleSelectRow = useCallback((fileId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  }, []);

  const handleDeselectAll = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const handleBulkDelete = useCallback(() => {
    onDelete(Array.from(selectedIds));
    setSelectedIds(new Set());
  }, [selectedIds, onDelete]);

  const handleBulkForward = useCallback(() => {
    const selected = files.filter((f) => selectedIds.has(f.id));
    onForward(selected);
  }, [selectedIds, files, onForward]);

  const handleFilterChange = useCallback((values: Record<string, FilterOptionCheckboxValue[] | FilterOptionDateRangeValue | FilterOptionNumberRangeValue>) => {
    const types = ((values['file-type'] || []) as FilterOptionCheckboxValue[]).map((v) => String(v.value));
    const statuses = ((values['file-status'] || []) as FilterOptionCheckboxValue[]).map((v) => String(v.value));
    const dateRange = values['file-date'] as FilterOptionDateRangeValue | undefined;
    setFilterTypes(types);
    setFilterStatuses(statuses);
    setFilterDateFrom(dateRange?.from);
    setFilterDateTo(dateRange?.to);
    setFilterOpen(false);
    setCurrentPage(1);
  }, []);

  const handleFilterClearAll = useCallback(() => {
    setFilterTypes([]);
    setFilterStatuses([]);
    setFilterDateFrom(undefined);
    setFilterDateTo(undefined);
    setFilterOpen(false);
    setCurrentPage(1);
  }, []);

  const isAllSelected = pagedFiles.length > 0 && selectedIds.size === pagedFiles.length;
  const isIndeterminate = selectedIds.size > 0 && selectedIds.size < pagedFiles.length;

  return (
    <Card>
      <CardHeader
        title="Files"
        subtitle={selectedIds.size > 0 ? `${selectedIds.size} file${selectedIds.size > 1 ? 's' : ''} selected` : ''}
        slotRight={
          <div className="flex items-center justify-end gap-4">
            {selectedIds.size > 0 ? (
              <Toolbar>
                <Button icon={<DownloadIcon />} variant="quiet-primary" onClick={() => {}}>
                  Download
                </Button>
                <Button icon={<SendIcon />} variant="quiet-primary" onClick={handleBulkForward}>
                  Forward
                </Button>
                <Button icon={<TrashIcon />} variant="quiet-primary" onClick={handleBulkDelete}>
                  Delete
                </Button>
                <Button icon={<CloseIcon />} variant="quiet-primary" onClick={handleDeselectAll}>
                  Deselect
                </Button>
              </Toolbar>
            ) : null}
            <Search
              id="file-search"
              placeholder="Search files..."
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            <Button
              variant="tertiary"
              icon={<FilterIcon />}
              onClick={() => setFilterOpen(true)}
            >
              Filters
            </Button>
          </div>
        }
      />
      <CardContent>
        {/* Filter Modal */}
        {filterOpen && (
          <Filter
            title="Filters"
            container="modal"
            onApply={handleFilterChange}
            onCancel={() => setFilterOpen(false)}
            onClearAll={handleFilterClearAll}
          >
            <FilterGroup
              id="file-type"
              label="File Type"
              type="checkbox"
              selected={filterTypes}
              options={[
                { label: 'PDF', value: 'pdf' },
                { label: 'Spreadsheet', value: 'spreadsheet' },
                { label: 'Other', value: 'document' },
                { label: 'Image', value: 'image' },
                { label: 'Archive', value: 'archive' },
              ]}
            />
            <FilterGroup
              id="file-status"
              label="Status"
              type="checkbox"
              selected={filterStatuses}
              options={[
                { label: 'Sent', value: 'sent' },
                { label: 'New', value: 'new' },
                { label: 'Pending', value: 'pending' },
                { label: 'Read', value: 'read' },
              ]}
            />
            <FilterGroup
              id="file-date"
              label="Date Range"
              type="date-range"
              dateRangeFrom={filterDateFrom}
              dateRangeTo={filterDateTo}
              options={[]}
            />
          </Filter>
        )}

        {/* Table */}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ width: 40 }}>
                <Checkbox
                  id="select-all"
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={handleSelectAll}
                  aria-label="Select all files"
                />
              </TableCell>
              <TableCell
                onClick={() => handleSort(undefined, 'name')}
                style={{ cursor: 'pointer' }}
              >
                File Name
              </TableCell>
              <TableCell
                onClick={() => handleSort(undefined, 'sender')}
                style={{ cursor: 'pointer' }}
              >
                Sender
              </TableCell>
              <TableCell
                onClick={() => handleSort(undefined, 'size')}
                style={{ cursor: 'pointer' }}
              >
                Size
              </TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Status</TableCell>
              <TableCell
                onClick={() => handleSort(undefined, 'date')}
                style={{ cursor: 'pointer' }}
              >
                Date
              </TableCell>
              <TableCell style={{ width: 120 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedFiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8}>
                  <div className="flex flex-col items-center py-8 text-color-tertiary">
                    <SearchIcon />
                    <p className="mt-2 text-sm">No files found</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              pagedFiles.map((file) => {
                const dirColor = directionBadgeColor[file.direction] || 'gray';
                const dirText = directionBadgeText[file.direction] || file.direction;
                const stsColor = statusBadgeColor[file.status] || 'gray';
                const stsText = statusBadgeText[file.status] || file.status;

                return (
                  <TableRow
                    key={file.id}
                    selected={file.id === selectedFileId}
                    onClick={() => onRowClick(file)}
                    style={{ cursor: 'pointer' }}
                  >
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        id={`select-${file.id}`}
                        checked={selectedIds.has(file.id)}
                        onChange={() => handleSelectRow(file.id)}
                        aria-label={`Select ${file.name}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <DescriptionIcon />
                        <span className="text-sm truncate max-w-[200px]">{file.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{file.sender.name}</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatFileSize(file.size)}</span>
                    </TableCell>
                    <TableCell>
                      <Badge color={dirColor}>{dirText}</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge color={stsColor}>{stsText}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDate(file.date)}</span>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="tertiary"
                          icon={<DescriptionIcon />}
                          aria-label="Preview"
                          onClick={() => onRowClick(file)}
                        />
                        <Button
                          variant="tertiary"
                          icon={<SendIcon />}
                          aria-label="Forward"
                          onClick={() => onForward([file])}
                        />
                        <Button
                          ref={(el: HTMLButtonElement | null) => {
                            getMenuRef(file.id).current = el;
                          }}
                          variant="tertiary"
                          icon={<MoreDotsIcon />}
                          aria-label="More actions"
                        />
                        <ContextualMenu
                          anchor={getMenuRef(file.id)}
                          position="bottom-right"
                        >
                          <ContextualMenuItem
                            as="button"
                            id={`star-${file.id}`}
                            label={file.starred ? 'Unstar' : 'Star'}
                            prefix={<FavoriteIcon />}
                            onClick={() => onToggleStar(file.id)}
                          />
                          <ContextualMenuItem
                            as="button"
                            id={`rename-${file.id}`}
                            label="Rename"
                            prefix={<EditIcon />}
                            onClick={() => {}}
                          />
                          <ContextualMenuItem
                            as="button"
                            id={`delete-${file.id}`}
                            label="Delete"
                            prefix={<TrashIcon />}
                            onClick={() => onDelete([file.id])}
                          />
                        </ContextualMenu>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        {sortedFiles.length > 0 && (
          <div className="mt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              totalItems={sortedFiles.length}
              onPageChange={(page) => setCurrentPage(page)}
              onPageSizeChange={handlePageSizeChange}
              showPageInfo
              showPageGoTo={false}
              showPageSizeList
              pageSizeListLabel="Files per page"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default FileTable;
